import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";

const CategoryPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { category, subCategory, subSubCategory, subSubSubCategory } =
    location.state || {};

  // State for modal visibility, checkbox, and the subcategory being interacted with
  const [showTermsModal, setShowTermsModal] = useState(false);
  const [pendingSubCategory, setPendingSubCategory] = useState(null);
  const [isTermsAccepted, setIsTermsAccepted] = useState(false);

  // Determine the current category level
  const currentCategory =
    subSubSubCategory || subSubCategory || subCategory || category;

  // Determine the next level of subcategories (if any)
  const subCategories =
    currentCategory?.subSubSubCategories ||
    currentCategory?.subSubCategories ||
    currentCategory?.subCategories ||
    [];

  // Check if terms and conditions should be shown for the current subcategory on page load
  useEffect(() => {
    if (
      subCategory &&
      !subSubCategory &&
      !subSubSubCategory &&
      subCategory?.termsAndConditions &&
      !sessionStorage.getItem(`termsAccepted_${subCategory?.subCategoryName}`)
    ) {
      setPendingSubCategory(subCategory);
      setShowTermsModal(true);
    }
  }, [subCategory, subSubCategory, subSubSubCategory]);

  // Helper function to check if a subcategory has products directly or in nested subcategories
  const hasProducts = (subCategory) => {
    if (subCategory?.products?.length > 0) return true;

    if (subCategory?.subSubCategories?.length > 0) {
      for (const subSubCategory of subCategory.subSubCategories) {
        if (subSubCategory?.products?.length > 0) return true;

        if (subSubCategory?.subSubSubCategories?.length > 0) {
          for (const subSubSubCategory of subSubCategory.subSubSubCategories) {
            if (subSubSubCategory?.products?.length > 0) return true;
          }
        }
      }
    }

    return false;
  };

  // Helper function to check if subcategory is enabled (has products and is active)
  const isEnabled = (subCategory) => {
    return hasProducts(subCategory) && subCategory?.isActive !== false;
  };

  // Determine the base path for navigation
  const getBasePath = () => {
    let path = `/category/${category?.categoryName
      ?.toLowerCase()
      .replace(/\s+/g, "-")}`;
    if (subCategory) {
      path += `/${subCategory?.subCategoryName
        ?.toLowerCase()
        .replace(/\s+/g, "-")}`;
    }
    if (subSubCategory) {
      path += `/${subSubCategory?.subSubCategoryName
        ?.toLowerCase()
        .replace(/\s+/g, "-")}`;
    }
    if (subSubSubCategory) {
      path += `/${subSubSubCategory?.subSubSubCategoryName
        ?.toLowerCase()
        .replace(/\s+/g, "-")}`;
    }
    return path;
  };

  // Redirect to SubCategoryPage if the current category has products and is active
  useEffect(() => {
    if (
      currentCategory?.products?.length > 0 &&
      currentCategory?.isActive !== false &&
      !location.pathname.endsWith("/products") &&
      !showTermsModal
    ) {
      navigate(`${getBasePath()}/products`, {
        state: {
          category,
          subCategory,
          subSubCategory,
          subSubSubCategory,
          currentCategory,
        },
        replace: true,
      });
    }
  }, [currentCategory, navigate, location.pathname, showTermsModal]);

  // Handle navigation to the next level
  const handleSubCategoryClick = (nextCategory) => {
    if (!isEnabled(nextCategory)) {
      return; // Do not navigate if not enabled
    }

    if (
      nextCategory?.subCategoryName &&
      nextCategory?.termsAndConditions &&
      !sessionStorage.getItem(`termsAccepted_${nextCategory.subCategoryName}`)
    ) {
      setPendingSubCategory(nextCategory);
      setShowTermsModal(true);
      return;
    }

    const basePath = getBasePath();
    const nextCategoryName =
      nextCategory.subSubSubCategoryName ||
      nextCategory.subSubCategoryName ||
      nextCategory.subCategoryName;
    const nextPath = `${basePath}/${nextCategoryName
      .toLowerCase()
      .replace(/\s+/g, "-")}`;

    const state = {
      category,
      subCategory,
      subSubCategory,
      subSubSubCategory,
      ...(subSubSubCategory
        ? { subSubSubCategory: nextCategory }
        : subSubCategory
        ? { subSubSubCategory: nextCategory }
        : subCategory
        ? { subSubCategory: nextCategory }
        : { subCategory: nextCategory }),
    };

    navigate(nextPath, { state });
  };

  // Handle terms acceptance
  const handleAcceptTerms = () => {
    if (pendingSubCategory?.subCategoryName && isTermsAccepted) {
      sessionStorage.setItem(
        `termsAccepted_${pendingSubCategory.subCategoryName}`,
        "true"
      );
      setShowTermsModal(false);
      setIsTermsAccepted(false);

      const basePath = `/category/${category?.categoryName
        ?.toLowerCase()
        .replace(/\s+/g, "-")}`;
      const nextPath = `${basePath}/${pendingSubCategory.subCategoryName
        .toLowerCase()
        .replace(/\s+/g, "-")}`;
      navigate(nextPath, {
        state: {
          category,
          subCategory: pendingSubCategory,
          subSubCategory: null,
          subSubSubCategory: null,
        },
      });
      setPendingSubCategory(null);
    }
  };

  // Handle modal cancellation
  const handleCancelTerms = () => {
    setShowTermsModal(false);
    setIsTermsAccepted(false);
    setPendingSubCategory(null);
    navigate(-1);
  };

  // Handle Enter key for accessibility
  const handleKeyDown = (e) => {
    if (e.key === "Enter" && isTermsAccepted) {
      handleAcceptTerms();
    }
  };

  return (
    <div className="w-full max-w-screen-2xl mx-auto px-4 sm:px-6 md:px-8 py-8 bg-white">
      {/* Terms and Conditions Modal */}
      {showTermsModal && pendingSubCategory?.termsAndConditions && (
        <div
          className="fixed inset-0 bg-opacity-30 backdrop-blur-md flex items-center justify-center z-50 transition-all duration-300"
          role="dialog"
          aria-labelledby="termsModalTitle"
          aria-modal="true"
        >
          <div className="bg-white rounded-2xl p-6 sm:p-8 max-w-lg w-full mx-4 shadow-xl border border-gray-100 border-opacity-50 bg-gradient-to-br from-white to-gray-50 transform transition-all duration-300 scale-100">
            <h3
              id="termsModalTitle"
              className="text-2xl sm:text-3xl font-bold text-[#303030] mb-4 font-poppins"
            >
              Terms for {pendingSubCategory?.subCategoryName}
            </h3>
            <div className="max-h-80 overflow-y-auto mb-6 pr-3 custom-scrollbar">
              <p className="text-gray-600 text-sm sm:text-base leading-relaxed font-poppins">
                {pendingSubCategory.termsAndConditions}
              </p>
            </div>
            <div className="flex items-center mb-6">
              <input
                type="checkbox"
                id="termsCheckbox"
                checked={isTermsAccepted}
                onChange={(e) => setIsTermsAccepted(e.target.checked)}
                className="hidden"
              />
              <label
                htmlFor="termsCheckbox"
                className={`flex items-center cursor-pointer select-none ${
                  isTermsAccepted ? "text-[#FF8500]" : "text-gray-500"
                }`}
              >
                <div
                  className={`w-5 h-5 border-2 rounded-md flex items-center justify-center mr-2 transition-all duration-200 ${
                    isTermsAccepted
                      ? "border-[#FF8500] bg-[#FF8500]"
                      : "border-gray-300"
                  }`}
                >
                  {isTermsAccepted && (
                    <svg
                      className="w-3 h-3 text-white"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                        d="M5 13l4 4L19 7"
                      ></path>
                    </svg>
                  )}
                </div>
                <span className="text-sm sm:text-base font-poppins">
                  I agree to the Terms and Conditions
                </span>
              </label>
            </div>
            <div className="flex justify-end space-x-4">
              <button
                className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg font-poppins font-medium text-sm hover:bg-gray-100 transition-colors duration-200"
                onClick={handleCancelTerms}
              >
                Cancel
              </button>
              <button
                className={`px-6 py-2 bg-gradient-to-r from-[#FF8500] to-[#e67600] text-white rounded-lg font-poppins font-medium text-sm hover:from-[#e67600] hover:to-[#d66b00] transition-colors duration-200 hover:shadow-md ${
                  !isTermsAccepted
                    ? "opacity-50 cursor-not-allowed"
                    : "animate-pulse-subtle"
                }`}
                onClick={handleAcceptTerms}
                onKeyDown={handleKeyDown}
                disabled={!isTermsAccepted}
                aria-disabled={!isTermsAccepted}
              >
                Accept
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Category Name and Description */}
      <div className="mt-8 text-center sm:text-left">
        <h4 className="text-xl sm:text-2xl md:text-3xl font-semibold text-[#303030] mb-6 text-center sm:text-left capitalize">
          {currentCategory?.categoryName ||
            currentCategory?.subCategoryName ||
            currentCategory?.subSubCategoryName ||
            currentCategory?.subSubSubCategoryName}
        </h4>
        <div className="bg-gray-50 p-6 rounded-lg shadow-sm">
          <p className="text-gray-600 text-sm sm:text-base md:text-lg leading-7">
            {currentCategory?.description}
          </p>
        </div>
      </div>

      {/* Decorative Divider */}
      <div className="my-8 border-t border-gray-200"></div>

      {/* Subcategories List */}
      {subCategories.length > 0 && (
        <div className="mb-8">
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 sm:gap-6">
            {subCategories.map((subCategory, index) => {
              const enabled = isEnabled(subCategory);
              return (
                <div
                  key={index}
                  aria-disabled={!enabled}
                  className={`subcategory-card bg-white border border-gray-200 rounded-lg shadow-sm p-4 transition-all duration-300 ${
                    enabled
                      ? "cursor-pointer hover:shadow-lg hover:scale-105"
                      : "opacity-50 cursor-not-allowed"
                  }`}
                  onClick={
                    enabled
                      ? () => handleSubCategoryClick(subCategory)
                      : undefined
                  }
                >
                  {(subCategory.subCategoryName ||
                    subCategory.subSubCategoryName ||
                    subCategory.subSubSubCategoryName) && (
                    <span
                      className={`status-badge ${
                        enabled ? "status-live" : "status-coming-soon"
                      }`}
                    >
                      {enabled ? "Live" : "Coming Soon"}
                    </span>
                  )}
                  {subCategory.images?.[0] && (
                    <div
                      className="w-full h-32 sm:h-40 rounded-md bg-cover bg-center mb-3"
                      style={{
                        backgroundImage: `url(${
                          subCategory.images[0] || "/placeholder.png"
                        })`,
                      }}
                    ></div>
                  )}
                  <h5 className="font-medium text-base sm:text-lg md:text-xl text-[#303030] line-clamp-2">
                    {subCategory?.subCategoryName ||
                      subCategory?.subSubCategoryName ||
                      subCategory?.subSubSubCategoryName}
                  </h5>
                  <p className="text-gray-500 text-xs sm:text-sm md:text-base mt-2 line-clamp-2">
                    {subCategory?.description ||
                      "Discover more in this subcategory."}
                  </p>
                  <button
                    className={`mt-3 text-[#FF8500] font-medium text-sm sm:text-base ${
                      enabled ? "hover:underline" : "cursor-not-allowed"
                    }`}
                    onClick={
                      enabled
                        ? (e) => {
                            e.stopPropagation();
                            handleSubCategoryClick(subCategory);
                          }
                        : undefined
                    }
                    disabled={!enabled}
                  >
                    Explore
                  </button>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Fallback for No Subcategories or Products */}
      {subCategories.length === 0 && !currentCategory?.products?.length && (
        <p className="text-gray-500 text-sm sm:text-base md:text-lg text-center">
          No subcategories or products available for this category.
        </p>
      )}

      <style>
        {`
          /* Base Styles */
          .subcategory-card {
            padding: 16px;
            border-radius: 12px;
            transition: transform 0.3s ease, box-shadow 0.3s ease;
            background-color: #ffffff;
            position: relative;
          }
          .subcategory-card:hover {
            transform: translateY(-5px);
            box-shadow: 0 6px 12px rgba(0, 0, 0, 0.15);
          }
          .subcategory-card h5 {
            font-family: 'Poppins', sans-serif;
            font-weight: 600;
          }
          .subcategory-card p {
            font-family: 'Poppins', sans-serif;
            font-weight: 400;
          }
          .subcategory-card button {
            font-family: 'Poppins', sans-serif;
            font-weight: 500;
          }
          .status-badge {
            position: absolute;
            top: 10px;
            left: 10px;
            padding: 4px 8px;
            border-radius: 4px;
            font-size: 12px;
            font-weight: 500;
            color: white;
            font-family: 'Poppins', sans-serif;
          }
          .status-live {
            background-color: #35A93E;
          }
          .status-coming-soon {
            background-color: #6B7280;
          }

          /* Custom Scrollbar for Modal */
          .custom-scrollbar::-webkit-scrollbar {
            width: 6px;
          }
          .custom-scrollbar::-webkit-scrollbar-track {
            background: #f3f4f6;
            border-radius: 3px;
          }
          .custom-scrollbar::-webkit-scrollbar-thumb {
            background: #FF8500;
            border-radius: 3px;
          }
          .custom-scrollbar::-webkit-scrollbar-thumb:hover {
            background: #e67600;
          }

          /* Subtle Pulse Animation for Accept Button */
          @keyframes pulse-subtle {
            0% {
              box-shadow: 0 0 0 0 rgba(255, 133, 0, 0.4);
            }
            70% {
              box-shadow: 0 0 0 10px rgba(255, 133, 0, 0);
            }
            100% {
              box-shadow: 0 0 0 0 rgba(255, 133, 0, 0);
            }
          }
          .animate-pulse-subtle {
            animation: pulse-subtle 2s infinite;
          }

          /* Tablet (577px - 768px) */
          @media (max-width: 768px) {
            .max-w-3xl {
              max-width: 32rem !important; /* max-w-2xl */
            }
            .text-3xl {
              font-size: 1.75rem !important;
            }
            .text-4xl {
              font-size: 2rem !important;
            }
            .text-5xl {
              font-size: 2.25rem !important;
            }
            .text-lg {
              font-size: 1rem !important;
            }
            .text-xl {
              font-size: 1.125rem !important;
            }
            .text-2xl {
              font-size: 1.25rem !important;
            }
            .text-base {
              font-size: 0.875rem !important;
            }
            .text-sm {
              font-size: 0.75rem !important;
            }
            .h-48 {
              height: 12rem !important;
            }
            .h-64 {
              height: 16rem !important;
            }
            .h-80 {
              height: 18rem !important;
            }
            .h-32 {
              height: 8rem !important;
            }
            .h-40 {
              height: 10rem !important;
            }
            .my-8 {
              margin-top: 1.5rem !important;
              margin-bottom: 1.5rem !important;
            }
            .subcategory-card {
              padding: 12px;
            }
            .bg-gray-50 {
              padding: 16px;
            }
            .status-badge {
              font-size: 10px;
              padding: 3px 6px;
            }
            .custom-scrollbar::-webkit-scrollbar {
              width: 5px;
            }
          }

          /* Mobile (up to 576px) */
          @media (max-width: 576px) {
            .max-w-3xl {
              max-width: 24rem !important; /* max-w-xl */
            }
            .text-3xl {
              font-size: 1.5rem !important;
            }
            .text-4xl {
              font-size: 1.75rem !important;
            }
            .text-5xl {
              font-size: 2rem !important;
            }
            .text-lg {
              font-size: 0.875rem !important;
            }
            .text-xl {
              font-size: 1rem !important;
            }
            .text-2xl {
              font-size: 1.125rem !important;
            }
            .text-base {
              font-size: 0.75rem !important;
            }
            .text-sm {
              font-size: 0.675rem !important;
            }
            .h-48 {
              height: 10rem !important;
            }
            .h-64 {
              height: 12rem !important;
            }
            .h-80 {
              height: 14rem !important;
            }
            .h-32 {
              height: 6rem !important;
            }
            .h-40 {
              height: 8rem !important;
            }
            .my-8 {
              margin-top: 1rem !important;
              margin-bottom: 1rem !important;
            }
            .subcategory-card {
              padding: 10px;
            }
            .bg-gray-50 {
              padding: 12px;
            }
            .text-center {
              text-align: center !important;
            }
            .status-badge {
              font-size: 8px;
              padding: 2px 4px;
            }
            .custom-scrollbar::-webkit-scrollbar {
              width: 4px;
            }
          }
        `}
      </style>
    </div>
  );
};

export default CategoryPage;